import { useQuery } from '@tanstack/react-query'
import { getEvent } from '@sportninja/common/services/event'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { EntityPermission } from '@sportninja/common/services/event/getEvent'
import type { SnEvent } from '../types'

interface UseEventReturn {
  data: SnEvent | null | undefined
  /** `meta.permissions[eventId]`, or null when the API does not send it. */
  permission: EntityPermission | null
  isLoading: boolean
  error: Error | null
  /**
   * Force a fetch, bypassing `staleTime`. Drives pull-to-refresh — without it
   * a detail screen re-entered inside the stale window keeps showing the
   * cached event, so an edit made elsewhere (or on another device) is invisible
   * until the window lapses.
   */
  refetch: () => Promise<unknown>
  /** True only while a REFETCH is in flight; false on the first load. */
  isRefetching: boolean
}

/**
 * A single event by id, for the detail page.
 *
 * Keeps `data` as the event itself so existing call sites read unchanged, and
 * exposes the response's permission metadata alongside it.
 */
export const useEvent = (eventId?: string): UseEventReturn => {
  'use memo'

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: queryKeys.events.byId(eventId),
    queryFn: () => getEvent(eventId as string),
    enabled: Boolean(eventId),
    staleTime: 1000 * 15,
  })

  return {
    data: data?.event,
    permission: data?.permission ?? null,
    isLoading,
    error: error as Error | null,
    refetch,
    isRefetching,
  }
}
